import { useState, useEffect, useCallback } from 'react';
import {
  FiShoppingCart, FiPlus, FiSearch, FiFilter, FiEye, FiEdit2, FiTrash2,
  FiChevronLeft, FiChevronRight, FiCheck, FiTruck, FiPackage, FiDollarSign,
  FiX, FiClock, FiAlertCircle, FiRefreshCw
} from 'react-icons/fi';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';

interface Commande {
  id: number; uuid: string; reference: string; client_id: number; commercial_id: number;
  date_commande: string; date_livraison_prevue: string | null; date_livraison_effective: string | null;
  statut: string; statut_paiement: string; montant_ht: number; montant_tva: number; montant_ttc: number;
  montant_remise: number; montant_paye: number; taux_tva: number; priorite: string;
  conditions_paiement: string | null; notes: string | null; source: string;
  client_nom: string; client_tel: string; commercial_nom: string; nb_lignes: number;
  lignes?: LigneCommande[]; paiements?: Paiement[];
  nom_contact?: string; prenom_contact?: string; client_email?: string; client_adresse?: string;
  adresse_livraison?: string; signature_base64?: string;
}

interface LigneCommande {
  id?: number; produit_id: number; produit_nom?: string; designation: string;
  quantite: number; prix_unitaire: number; remise_pct: number; montant_ligne: number;
  unite?: string; notes?: string;
}

interface Paiement {
  id: number; montant: number; mode_paiement: string; reference_paiement: string;
  date_paiement: string; notes: string;
}

interface Client { id: number; raison_sociale: string; nom_contact: string; telephone: string; adresse: string; }
interface Produit { id: number; nom: string; prix: number; unite: string; }
interface Stats {
  total_commandes: number; en_attente: number; confirmees: number; livrees: number;
  ca_total: number; ca_mois: number; ca_jour: number; total_paye: number; total_impaye: number;
}

const statutConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  brouillon: { label: 'Brouillon', color: '#94A3B8', icon: <FiEdit2 size={14} /> },
  en_attente: { label: 'En attente', color: '#F59E0B', icon: <FiClock size={14} /> },
  confirmee: { label: 'Confirmee', color: '#3B82F6', icon: <FiCheck size={14} /> },
  en_preparation: { label: 'Preparation', color: '#8B5CF6', icon: <FiPackage size={14} /> },
  expediee: { label: 'Expediee', color: '#06B6D4', icon: <FiTruck size={14} /> },
  livree: { label: 'Livree', color: '#16A34A', icon: <FiCheck size={14} /> },
  annulee: { label: 'Annulee', color: '#EF4444', icon: <FiX size={14} /> },
};

const paiementConfig: Record<string, { label: string; color: string }> = {
  non_paye: { label: 'Non paye', color: '#EF4444' },
  partiel: { label: 'Partiel', color: '#F59E0B' },
  paye: { label: 'Paye', color: '#16A34A' },
};

export default function Commandes() {
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterPaiement, setFilterPaiement] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Commande | null>(null);
  const [showPaiementModal, setShowPaiementModal] = useState<Commande | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [produits, setProduits] = useState<Produit[]>([]);

  // Form state
  const [formClientId, setFormClientId] = useState<number>(0);
  const [formLignes, setFormLignes] = useState<LigneCommande[]>([]);
  const [formNotes, setFormNotes] = useState('');
  const [formPriorite, setFormPriorite] = useState('normale');
  const [formDateLivraison, setFormDateLivraison] = useState('');
  const [formConditions, setFormConditions] = useState('');
  const [formRemise, setFormRemise] = useState(0);
  const [formTva, setFormTva] = useState(18);
  const [formAdresseLivraison, setFormAdresseLivraison] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [clientSearch, setClientSearch] = useState('');

  // Paiement form
  const [pMontant, setPMontant] = useState('');
  const [pMode, setPMode] = useState('especes');
  const [pRef, setPRef] = useState('');

  const fetchCommandes = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit: 20 };
      if (search) params.search = search;
      if (filterStatut) params.statut = filterStatut;
      if (filterPaiement) params.statut_paiement = filterPaiement;
      const res = await api.get('/commandes', { params });
      setCommandes(res.data.data);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [page, search, filterStatut, filterPaiement]);

  const fetchStats = async () => {
    try { const res = await api.get('/commandes/stats'); setStats(res.data); } catch (err) { console.error(err); }
  };

  const fetchClients = async () => {
    try { const res = await api.get('/clients', { params: { limit: 500 } }); setClients(res.data.data || res.data); } catch (err) { console.error(err); }
  };

  const fetchProduits = async () => {
    try { const res = await api.get('/produits', { params: { actif: 1, limit: 500 } }); setProduits(res.data.data || res.data); } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchCommandes(); }, [fetchCommandes]);
  useEffect(() => { fetchStats(); fetchClients(); fetchProduits(); }, []);

  const openCreate = () => {
    setEditingId(null); setFormClientId(0); setFormLignes([]); setFormNotes(''); setFormPriorite('normale');
    setFormDateLivraison(''); setFormConditions(''); setFormRemise(0); setFormTva(18); setFormAdresseLivraison('');
    setClientSearch(''); setShowModal(true);
  };

  const openEdit = async (cmd: Commande) => {
    try {
      const res = await api.get(`/commandes/${cmd.id}`);
      const detail = res.data;
      setEditingId(cmd.id); setFormClientId(detail.client_id);
      setFormLignes(detail.lignes.map((l: LigneCommande) => ({ ...l, produit_nom: l.designation })));
      setFormNotes(detail.notes || ''); setFormPriorite(detail.priorite);
      setFormDateLivraison(detail.date_livraison_prevue?.split('T')[0] || '');
      setFormConditions(detail.conditions_paiement || ''); setFormRemise(detail.montant_remise || 0);
      setFormTva(detail.taux_tva || 18); setFormAdresseLivraison(detail.adresse_livraison || '');
      setShowModal(true);
    } catch (err) { console.error(err); }
  };

  const openDetail = async (cmd: Commande) => {
    try { const res = await api.get(`/commandes/${cmd.id}`); setShowDetail(res.data); } catch (err) { console.error(err); }
  };

  const addLigne = () => {
    setFormLignes([...formLignes, { produit_id: 0, designation: '', quantite: 1, prix_unitaire: 0, remise_pct: 0, montant_ligne: 0 }]);
  };

  const updateLigne = (index: number, field: keyof LigneCommande, value: unknown) => {
    const updated = [...formLignes];
    // @ts-ignore
    updated[index][field] = value;
    if (field === 'produit_id') {
      const prod = produits.find(p => p.id === Number(value));
      if (prod) { updated[index].designation = prod.nom; updated[index].prix_unitaire = prod.prix; }
    }
    updated[index].montant_ligne = updated[index].quantite * updated[index].prix_unitaire * (1 - updated[index].remise_pct / 100);
    setFormLignes(updated);
  };

  const removeLigne = (index: number) => setFormLignes(formLignes.filter((_, i) => i !== index));

  const formTotalHT = formLignes.reduce((s, l) => s + (l.quantite * l.prix_unitaire * (1 - (l.remise_pct || 0) / 100)), 0) - formRemise;
  const formTotalTVA = formTotalHT * formTva / 100;
  const formTotalTTC = formTotalHT + formTotalTVA;

  const handleSave = async () => {
    if (!formClientId || formLignes.length === 0) return;
    setFormSaving(true);
    try {
      const payload = {
        client_id: formClientId, lignes: formLignes.map(l => ({
          produit_id: l.produit_id, designation: l.designation, quantite: l.quantite,
          prix_unitaire: l.prix_unitaire, remise_pct: l.remise_pct
        })),
        notes: formNotes || null, priorite: formPriorite,
        date_livraison_prevue: formDateLivraison || null, conditions_paiement: formConditions || null,
        montant_remise: formRemise, taux_tva: formTva, adresse_livraison: formAdresseLivraison || null, source: 'web'
      };
      if (editingId) await api.put(`/commandes/${editingId}`, payload);
      else await api.post('/commandes', payload);
      setShowModal(false); fetchCommandes(); fetchStats();
    } catch (err) { console.error(err); }
    setFormSaving(false);
  };

  const handleChangeStatut = async (id: number, statut: string) => {
    try { await api.put(`/commandes/${id}`, { statut }); fetchCommandes(); fetchStats(); if (showDetail) openDetail(showDetail); } catch (err) { console.error(err); }
  };

  const handleCancel = async (id: number) => {
    if (!confirm('Annuler cette commande ?')) return;
    try { await api.delete(`/commandes/${id}`); fetchCommandes(); fetchStats(); setShowDetail(null); } catch (err) { console.error(err); }
  };

  const handlePaiement = async () => {
    if (!showPaiementModal || !pMontant) return;
    try {
      await api.post(`/commandes/${showPaiementModal.id}/paiements`, { montant: parseFloat(pMontant), mode_paiement: pMode, reference_paiement: pRef || null });
      setShowPaiementModal(null); setPMontant(''); setPRef(''); fetchCommandes(); fetchStats();
    } catch (err) { console.error(err); }
  };

  const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' F';
  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const filteredClients = clientSearch ? clients.filter(c => c.raison_sociale.toLowerCase().includes(clientSearch.toLowerCase()) || c.nom_contact?.toLowerCase().includes(clientSearch.toLowerCase())) : clients;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiShoppingCart /> Commandes
          </h1>
          <p style={{ color: '#64748B', marginTop: 4 }}>{total} commande{total > 1 ? 's' : ''}</p>
        </div>
        <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#16A34A', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          <FiPlus size={18} /> Nouvelle commande
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'CA du mois', value: formatMoney(stats.ca_mois), color: '#16A34A', icon: <FiDollarSign /> },
            { label: 'En attente', value: stats.en_attente, color: '#F59E0B', icon: <FiClock /> },
            { label: 'Confirmees', value: stats.confirmees, color: '#3B82F6', icon: <FiCheck /> },
            { label: 'Livrees', value: stats.livrees, color: '#16A34A', icon: <FiTruck /> },
            { label: 'Impayes', value: formatMoney(stats.total_impaye), color: '#EF4444', icon: <FiAlertCircle /> },
          ].map((s, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#64748B', fontSize: 13 }}>{s.label}</span>
                <span style={{ color: s.color }}>{s.icon}</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, marginTop: 8, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
          <input placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }} />
        </div>
        <select value={filterStatut} onChange={e => { setFilterStatut(e.target.value); setPage(1); }}
          style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, minWidth: 150 }}>
          <option value="">Tous statuts</option>
          {Object.entries(statutConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={filterPaiement} onChange={e => { setFilterPaiement(e.target.value); setPage(1); }}
          style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, minWidth: 150 }}>
          <option value="">Tous paiements</option>
          {Object.entries(paiementConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => { fetchCommandes(); fetchStats(); }} style={{ padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
          <FiRefreshCw size={16} />
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
              {['Reference', 'Client', 'Commercial', 'Date', 'Montant TTC', 'Statut', 'Paiement', 'Actions'].map(h =>
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#64748B', fontSize: 12, textTransform: 'uppercase' }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Chargement...</td></tr>
            ) : commandes.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Aucune commande</td></tr>
            ) : commandes.map(cmd => {
              const st = statutConfig[cmd.statut] || statutConfig.brouillon;
              const pa = paiementConfig[cmd.statut_paiement] || paiementConfig.non_paye;
              return (
                <tr key={cmd.id} style={{ borderBottom: '1px solid #F1F5F9', cursor: 'pointer' }} onClick={() => openDetail(cmd)}>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1E293B' }}>{cmd.reference}</td>
                  <td style={{ padding: '12px 14px' }}>{cmd.client_nom}</td>
                  <td style={{ padding: '12px 14px', color: '#64748B' }}>{cmd.commercial_nom}</td>
                  <td style={{ padding: '12px 14px', color: '#64748B' }}>{formatDate(cmd.date_commande)}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600 }}>{formatMoney(cmd.montant_ttc)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: st.color + '18', color: st.color }}>
                      {st.icon} {st.label}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: pa.color + '18', color: pa.color }}>{pa.label}</span>
                  </td>
                  <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openDetail(cmd)} style={{ padding: 6, border: 'none', background: '#EFF6FF', borderRadius: 6, cursor: 'pointer', color: '#3B82F6' }} title="Voir"><FiEye size={15} /></button>
                      {!['livree', 'annulee'].includes(cmd.statut) && (
                        <>
                          <button onClick={() => openEdit(cmd)} style={{ padding: 6, border: 'none', background: '#FFF7ED', borderRadius: 6, cursor: 'pointer', color: '#F59E0B' }} title="Modifier"><FiEdit2 size={15} /></button>
                          <button onClick={() => handleCancel(cmd.id)} style={{ padding: 6, border: 'none', background: '#FEF2F2', borderRadius: 6, cursor: 'pointer', color: '#EF4444' }} title="Annuler"><FiTrash2 size={15} /></button>
                        </>
                      )}
                      <button onClick={() => { setShowPaiementModal(cmd); setPMontant(''); setPRef(''); }} style={{ padding: 6, border: 'none', background: '#F0FDF4', borderRadius: 6, cursor: 'pointer', color: '#16A34A' }} title="Paiement"><FiDollarSign size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20, alignItems: 'center' }}>
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer' }}><FiChevronLeft /></button>
          <span style={{ fontSize: 14, color: '#64748B' }}>Page {page} / {Math.ceil(total / 20)}</span>
          <button onClick={() => setPage(page + 1)} disabled={page * 20 >= total} style={{ padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#fff', cursor: 'pointer' }}><FiChevronRight /></button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '90%', maxWidth: 900, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700 }}>{editingId ? 'Modifier la commande' : 'Nouvelle commande'}</h2>
              <button onClick={() => setShowModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20 }}><FiX /></button>
            </div>

            {/* Client */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#64748B', display: 'block', marginBottom: 6 }}>Client *</label>
              <SearchableSelect
                value={formClientId > 0 ? String(formClientId) : ''}
                onChange={(v: string) => { setFormClientId(Number(v) || 0); setClientSearch(''); }}
                placeholder="Rechercher un client..."
                options={clients.map((c: any) => ({ value: String(c.id), label: c.raison_sociale, sub: c.nom_contact || '' }))}
              />
            </div>

            {/* Lignes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#64748B' }}>Produits *</label>
                <button onClick={addLigne} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#3B82F6', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  <FiPlus size={14} /> Ajouter
                </button>
              </div>
              {formLignes.map((ligne, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <select value={ligne.produit_id} onChange={e => updateLigne(idx, 'produit_id', parseInt(e.target.value))}
                    style={{ padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }}>
                    <option value={0}>-- Produit --</option>
                    {produits.map(p => <option key={p.id} value={p.id}>{p.nom} ({formatMoney(p.prix)})</option>)}
                  </select>
                  <input type="number" min={1} placeholder="Qte" value={ligne.quantite} onChange={e => updateLigne(idx, 'quantite', parseFloat(e.target.value) || 0)}
                    style={{ padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, textAlign: 'center' }} />
                  <input type="number" placeholder="P.U." value={ligne.prix_unitaire} onChange={e => updateLigne(idx, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                    style={{ padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, textAlign: 'right' }} />
                  <input type="number" min={0} max={100} placeholder="Rem%" value={ligne.remise_pct} onChange={e => updateLigne(idx, 'remise_pct', parseFloat(e.target.value) || 0)}
                    style={{ padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, textAlign: 'center' }} />
                  <span style={{ fontWeight: 600, fontSize: 13, textAlign: 'right' }}>{formatMoney(ligne.quantite * ligne.prix_unitaire * (1 - (ligne.remise_pct || 0) / 100))}</span>
                  <button onClick={() => removeLigne(idx)} style={{ border: 'none', background: '#FEF2F2', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#EF4444' }}><FiTrash2 size={14} /></button>
                </div>
              ))}
            </div>

            {/* Totals & Options */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Priorite</label>
                    <select value={formPriorite} onChange={e => setFormPriorite(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }}>
                      <option value="basse">Basse</option><option value="normale">Normale</option><option value="haute">Haute</option><option value="urgente">Urgente</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Date livraison</label>
                    <input type="date" value={formDateLivraison} onChange={e => setFormDateLivraison(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Remise globale (F)</label>
                    <input type="number" value={formRemise} onChange={e => setFormRemise(parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>TVA (%)</label>
                    <input type="number" value={formTva} onChange={e => setFormTva(parseFloat(e.target.value) || 0)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Conditions paiement</label>
                  <input value={formConditions} onChange={e => setFormConditions(e.target.value)} placeholder="Ex: 30 jours net" style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13 }} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Notes</label>
                  <textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} rows={2} style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ background: '#F8FAFC', borderRadius: 12, padding: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Recapitulatif</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: '#64748B' }}>Sous-total</span>
                  <span>{formatMoney(formLignes.reduce((s, l) => s + l.quantite * l.prix_unitaire * (1 - (l.remise_pct || 0) / 100), 0))}</span>
                </div>
                {formRemise > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, color: '#EF4444' }}>
                    <span>Remise</span><span>-{formatMoney(formRemise)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: '#64748B' }}>HT</span><span>{formatMoney(formTotalHT)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: '#64748B' }}>TVA ({formTva}%)</span><span>{formatMoney(formTotalTVA)}</span>
                </div>
                <div style={{ borderTop: '2px solid #E2E8F0', paddingTop: 12, marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 20, fontWeight: 700, color: '#16A34A' }}>
                  <span>TTC</span><span>{formatMoney(formTotalTTC)}</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 24px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', fontSize: 14, cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleSave} disabled={formSaving || !formClientId || formLignes.length === 0}
                style={{ padding: '10px 24px', border: 'none', borderRadius: 10, background: '#16A34A', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: formSaving ? 0.6 : 1 }}>
                {formSaving ? 'Enregistrement...' : editingId ? 'Modifier' : 'Creer la commande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '90%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700 }}>{showDetail.reference}</h2>
                <p style={{ color: '#64748B', fontSize: 14 }}>Creee le {formatDate(showDetail.date_commande)}</p>
              </div>
              <button onClick={() => setShowDetail(null)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}><FiX size={22} /></button>
            </div>

            {/* Status & Actions */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <span style={{ padding: '6px 14px', borderRadius: 20, fontWeight: 600, fontSize: 13, background: (statutConfig[showDetail.statut]?.color || '#94A3B8') + '18', color: statutConfig[showDetail.statut]?.color || '#94A3B8' }}>
                {statutConfig[showDetail.statut]?.label}
              </span>
              <span style={{ padding: '6px 14px', borderRadius: 20, fontWeight: 600, fontSize: 13, background: (paiementConfig[showDetail.statut_paiement]?.color || '#94A3B8') + '18', color: paiementConfig[showDetail.statut_paiement]?.color || '#94A3B8' }}>
                {paiementConfig[showDetail.statut_paiement]?.label}
              </span>
              {showDetail.statut === 'en_attente' && <button onClick={() => handleChangeStatut(showDetail.id, 'confirmee')} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, background: '#3B82F6', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Confirmer</button>}
              {showDetail.statut === 'confirmee' && <button onClick={() => handleChangeStatut(showDetail.id, 'en_preparation')} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, background: '#8B5CF6', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>En preparation</button>}
              {showDetail.statut === 'en_preparation' && <button onClick={() => handleChangeStatut(showDetail.id, 'expediee')} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, background: '#06B6D4', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Expedier</button>}
              {showDetail.statut === 'expediee' && <button onClick={() => handleChangeStatut(showDetail.id, 'livree')} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, background: '#16A34A', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Marquer livree</button>}
              {!['livree', 'annulee'].includes(showDetail.statut) && <button onClick={() => handleCancel(showDetail.id)} style={{ padding: '6px 14px', border: 'none', borderRadius: 20, background: '#FEF2F2', color: '#EF4444', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Annuler</button>}
            </div>

            {/* Client & Commercial info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 14 }}>
                <h4 style={{ fontSize: 12, textTransform: 'uppercase', color: '#94A3B8', marginBottom: 8 }}>Client</h4>
                <p style={{ fontWeight: 600 }}>{showDetail.client_nom}</p>
                <p style={{ fontSize: 13, color: '#64748B' }}>{showDetail.client_tel}</p>
                {showDetail.client_email && <p style={{ fontSize: 13, color: '#64748B' }}>{showDetail.client_email}</p>}
              </div>
              <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 14 }}>
                <h4 style={{ fontSize: 12, textTransform: 'uppercase', color: '#94A3B8', marginBottom: 8 }}>Commercial</h4>
                <p style={{ fontWeight: 600 }}>{showDetail.commercial_nom}</p>
                <p style={{ fontSize: 13, color: '#64748B' }}>Priorite: {showDetail.priorite}</p>
                {showDetail.date_livraison_prevue && <p style={{ fontSize: 13, color: '#64748B' }}>Livraison prevue: {formatDate(showDetail.date_livraison_prevue)}</p>}
              </div>
            </div>

            {/* Lignes */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left' }}>Produit</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Qte</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>P.U.</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center' }}>Rem.</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {showDetail.lignes?.map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '10px 12px' }}>{l.designation || l.produit_nom}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{l.quantite} {l.unite || ''}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatMoney(l.prix_unitaire)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: l.remise_pct > 0 ? '#EF4444' : '#94A3B8' }}>{l.remise_pct}%</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>{formatMoney(l.montant_ligne)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <div style={{ width: 280, background: '#F8FAFC', borderRadius: 10, padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}><span style={{ color: '#64748B' }}>HT</span><span>{formatMoney(showDetail.montant_ht)}</span></div>
                {showDetail.montant_remise > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: '#EF4444' }}><span>Remise</span><span>-{formatMoney(showDetail.montant_remise)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14 }}><span style={{ color: '#64748B' }}>TVA ({showDetail.taux_tva}%)</span><span>{formatMoney(showDetail.montant_tva)}</span></div>
                <div style={{ borderTop: '2px solid #E2E8F0', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, color: '#16A34A' }}><span>TTC</span><span>{formatMoney(showDetail.montant_ttc)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 13, color: '#64748B' }}><span>Paye</span><span style={{ color: '#16A34A' }}>{formatMoney(showDetail.montant_paye)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#EF4444', fontWeight: 600 }}><span>Reste</span><span>{formatMoney(showDetail.montant_ttc - showDetail.montant_paye)}</span></div>
              </div>
            </div>

            {showDetail.notes && <div style={{ background: '#FFFBEB', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}><strong>Notes :</strong> {showDetail.notes}</div>}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaiementModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Enregistrer un paiement</h3>
            <p style={{ fontSize: 13, color: '#64748B', marginBottom: 16 }}>Commande: <strong>{showPaiementModal.reference}</strong> - Reste: <strong style={{ color: '#EF4444' }}>{formatMoney(showPaiementModal.montant_ttc - showPaiementModal.montant_paye)}</strong></p>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Montant *</label>
              <input type="number" value={pMontant} onChange={e => setPMontant(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Mode de paiement *</label>
              <select value={pMode} onChange={e => setPMode(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }}>
                <option value="especes">Especes</option><option value="cheque">Cheque</option><option value="virement">Virement</option><option value="mobile_money">Mobile Money</option><option value="carte">Carte</option>
              </select>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Reference</label>
              <input value={pRef} onChange={e => setPRef(e.target.value)} placeholder="N° cheque, transaction..." style={{ width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowPaiementModal(null)} style={{ padding: '10px 20px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Annuler</button>
              <button onClick={handlePaiement} disabled={!pMontant} style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#16A34A', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
